// Reunderwriting Module (Tier 3) PR 4 — Metrics dashboard.
// Tabbed surface scoped to reunderwriting cases. Mirrors Deal Flow's
// MetricsDashboard shape, but the section list is narrower: Pipeline,
// Quality, Failures, Time-to-Completion. Filter state is URL-synced via
// useSearchParams so deep links share scope.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertOctagon,
  BarChart3,
  Clock,
  Filter as FilterIcon,
  ShieldCheck,
} from "lucide-react";
import { Card, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import {
  REUNDERWRITING_STAGE_LABEL,
  type ReunderwritingMetricsFilter,
  type ReunderwritingStage,
} from "@/types/reunderwriting";
import { PipelineSection } from "./metrics/PipelineSection";
import { QualitySection } from "./metrics/QualitySection";
import { FailuresSection } from "./metrics/FailuresSection";
import { TimeToCompletionSection } from "./metrics/TimeToCompletionSection";

type TabId = "pipeline" | "quality" | "failures" | "time-to-completion";

const TAB_VALUES: TabId[] = ["pipeline", "quality", "failures", "time-to-completion"];
const STAGES: ReunderwritingStage[] = [
  "Underwriting",
  "Closing",
  "Closed",
  "Declined",
  "Withdrawn",
];

function parseTab(v: string | null): TabId {
  return TAB_VALUES.find((t) => t === v) ?? "pipeline";
}
function parseStage(v: string | null): ReunderwritingStage | undefined {
  return STAGES.find((s) => s === v);
}

export function ReunderwritingMetricsDashboard() {
  const flagOn = useReunderwritingV1();
  const [params, setParams] = useSearchParams();

  const tab = parseTab(params.get("tab"));
  const filter = useMemo<ReunderwritingMetricsFilter>(
    () => ({
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      stage: parseStage(params.get("stage")),
      eventTypeCode: params.get("eventTypeCode") ?? undefined,
      analyst: params.get("analyst") ?? undefined,
    }),
    [params],
  );

  if (!flagOn) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Reunderwriting disabled</h2>
        <p className="text-small text-gray-mid">
          The Reunderwriting module is not enabled in this environment.
        </p>
      </Card>
    );
  }

  const setFilter = (next: Partial<Record<string, string | null>>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === undefined || v === "") merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged, { replace: false });
  };

  const items: EntityTabItem<TabId>[] = [
    { key: "pipeline", label: "Pipeline", icon: FilterIcon },
    { key: "quality", label: "Quality", icon: ShieldCheck },
    { key: "failures", label: "Failures", icon: AlertOctagon },
    { key: "time-to-completion", label: "Time to Completion", icon: Clock },
  ];

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-metrics">
      <PageHeader
        eyebrow="Reunderwriting"
        title="Metrics"
        description="Aggregated performance across the reunderwriting lifecycle. Click any chart to drill through."
        actions={
          <span className="inline-flex items-center gap-space-1 text-caption text-gray-mid">
            <BarChart3 size={14} aria-hidden />
            Live
          </span>
        }
      />

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="From" htmlFor="ru-metrics-from">
            <Input
              id="ru-metrics-from"
              type="date"
              value={filter.from ?? ""}
              onChange={(e) => setFilter({ from: e.target.value || null })}
            />
          </FormField>
          <FormField label="To" htmlFor="ru-metrics-to">
            <Input
              id="ru-metrics-to"
              type="date"
              value={filter.to ?? ""}
              onChange={(e) => setFilter({ to: e.target.value || null })}
            />
          </FormField>
          <FormField label="Stage" htmlFor="ru-metrics-stage">
            <Select
              id="ru-metrics-stage"
              value={filter.stage ?? ""}
              onChange={(e) => setFilter({ stage: e.target.value || null })}
              data-testid="ru-metrics-stage"
            >
              <option value="">All stages</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {REUNDERWRITING_STAGE_LABEL[s]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Event type" htmlFor="ru-metrics-event">
            <Input
              id="ru-metrics-event"
              placeholder="e.g. ASSUMPTION_NEW_BUYER"
              value={filter.eventTypeCode ?? ""}
              onChange={(e) =>
                setFilter({ eventTypeCode: e.target.value || null })
              }
            />
          </FormField>
        </div>
      </Card>

      <EntityTabBar
        ariaLabel="Reunderwriting metrics sections"
        testIdPrefix="ru-metrics"
        items={items}
        value={tab}
        onChange={(t) => setFilter({ tab: t })}
      />

      <div className="pt-space-3">
        {tab === "pipeline" && <PipelineSection filter={filter} />}
        {tab === "quality" && <QualitySection filter={filter} />}
        {tab === "failures" && <FailuresSection filter={filter} />}
        {tab === "time-to-completion" && <TimeToCompletionSection filter={filter} />}
      </div>
    </div>
  );
}
